/**
 * <s-product-gallery> — adds slide controls to the product page's swipeable image track.
 *
 * The track stays a native scroll-snap container (swipe works without JS). This component
 * wires the prev/next arrows, dots and thumbnails to scroll to a slide, keeps the active
 * dot/thumbnail/counter in sync while the buyer swipes, and plays a one-time "peek" so
 * buyers notice there are more photos. The peek is skipped for reduced-motion users.
 *
 * Expected DOM:
 *   <s-product-gallery>
 *     <div data-gallery-track> …slides… </div>
 *     <button data-gallery-prev> <button data-gallery-next>
 *     <button data-gallery-go="0"> … (dots and thumbnails)
 *     <span data-gallery-current>
 *   </s-product-gallery>
 */
class SProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;
    this.slides = Array.from(this.track.children);
    if (this.slides.length < 2) return;

    this.prevButton = this.querySelector('[data-gallery-prev]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    this.goButtons = Array.from(this.querySelectorAll('[data-gallery-go]'));
    this.counter = this.querySelector('[data-gallery-current]');
    this.index = 0;

    this.onPrev = () => this.goTo(this.index - 1);
    this.onNext = () => this.goTo(this.index + 1);
    this.onGo = (event) => this.goTo(Number(event.currentTarget.dataset.galleryGo));
    this.onScroll = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.sync());
    };
    this.onInteract = () => this.setAttribute('data-interacted', '');

    this.prevButton?.addEventListener('click', this.onPrev);
    this.nextButton?.addEventListener('click', this.onNext);
    this.goButtons.forEach((button) => button.addEventListener('click', this.onGo));
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('pointerdown', this.onInteract, { passive: true });

    this.sync();
    this.setupPeek();
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener('click', this.onPrev);
    this.nextButton?.removeEventListener('click', this.onNext);
    this.goButtons?.forEach((button) => button.removeEventListener('click', this.onGo));
    this.track?.removeEventListener('scroll', this.onScroll);
    this.track?.removeEventListener('pointerdown', this.onInteract);
    this.observer?.disconnect();
    clearTimeout(this.peekTimer);
    cancelAnimationFrame(this.frame);
  }

  /** True while the track is a horizontal slider (phones); on desktop it is a grid. */
  isSliding() {
    return this.track.scrollWidth > this.track.clientWidth + 1;
  }

  goTo(target) {
    const count = this.slides.length;
    const next = ((target % count) + count) % count;
    this.onInteract();
    if (this.isSliding()) {
      this.track.scrollTo({ left: this.slides[next].offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    } else {
      this.slides[next].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    this.setActive(next);
  }

  sync() {
    if (!this.isSliding()) return;
    const width = this.track.clientWidth || 1;
    this.setActive(Math.round(this.track.scrollLeft / width));
  }

  setActive(index) {
    const clamped = Math.max(0, Math.min(this.slides.length - 1, index));
    this.index = clamped;
    this.goButtons.forEach((button) => {
      const active = Number(button.dataset.galleryGo) === clamped;
      button.toggleAttribute('data-active', active);
      if (active) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });
    if (this.counter) this.counter.textContent = String(clamped + 1);
  }

  /** Slide the track a little and back once it is on screen, hinting that it swipes. */
  setupPeek() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.observer.disconnect();
      this.peekTimer = setTimeout(() => {
        if (!this.isSliding() || this.hasAttribute('data-interacted') || this.track.scrollLeft > 1) return;
        this.track.style.scrollSnapType = 'none';
        this.track.scrollTo({ left: this.track.clientWidth * 0.22, behavior: 'smooth' });
        this.peekTimer = setTimeout(() => {
          this.track.scrollTo({ left: 0, behavior: 'smooth' });
          this.peekTimer = setTimeout(() => {
            this.track.style.scrollSnapType = '';
          }, 500);
        }, 550);
      }, 900);
    }, { threshold: 0.6 });
    this.observer.observe(this.track);
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
